import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
  ConfirmDialog,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconDelete,
  IconGeneral,
  IconMetadata,
  IconRelationships,
  IconSpark,
} from './components/AdminIcons';

interface MyOrganization {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationSummary {
  organizationId: number;
  name: string;
  slug: string;
}

interface JoinRequestItem {
  id: number;
  organizationId: number;
  requestedByUserId: number;
  requestedByAuth0Sub: string;
  requestedByEmail: string;
  requestedRoleKey: string;
  status: string;
  message: string | null;
  createdAt: string;
}

interface InviteItem {
  id: number;
  organizationId: number;
  email: string;
  roleKey: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  acceptedByUserId: number | null;
}

const ROLE_OPTIONS = ['Owner', 'Principal', 'Teacher', 'Parent', 'Student'];
const ALLOWED_REQUEST_ORG_ROLES = ['owner', 'principal', 'teacher'];

const hasRequestAccessRole = (roles: string[]): boolean =>
  roles.some((role) => ALLOWED_REQUEST_ORG_ROLES.includes(role.trim().toLowerCase()));

type RowTypeFilter = 'all' | 'requests' | 'invites';
type RequestSortKey = 'user' | 'role' | 'status' | 'created';
type InviteSortKey = 'email' | 'role' | 'status' | 'expires';

interface JoinRequestRow {
  id: number;
  user: string;
  role: string;
  status: string;
  created: string;
  message: string;
  request: JoinRequestItem;
}

interface InviteRow {
  id: number;
  email: string;
  role: string;
  status: string;
  expires: string;
  invite: InviteItem;
}

const ManageOrganizationRequests: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [myOrganizations, setMyOrganizations] = useState<MyOrganization[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<OrganizationSummary[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);

  const [joinRequests, setJoinRequests] = useState<JoinRequestItem[]>([]);
  const [invites, setInvites] = useState<InviteItem[]>([]);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleKey, setInviteRoleKey] = useState('Student');

  const [searchValue, setSearchValue] = useState('');
  const [rowTypeFilter, setRowTypeFilter] = useState<RowTypeFilter>('all');
  const [selectedKind, setSelectedKind] = useState<'request' | 'invite' | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [pendingRejectRequestId, setPendingRejectRequestId] = useState<number | null>(null);

  const [requestPage, setRequestPage] = useState(1);
  const [invitePage, setInvitePage] = useState(1);
  const [requestSelectedIds, setRequestSelectedIds] = useState<number[]>([]);
  const [inviteSelectedIds, setInviteSelectedIds] = useState<number[]>([]);
  const [requestSortKey, setRequestSortKey] = useState<RequestSortKey>('created');
  const [requestSortDirection, setRequestSortDirection] = useState<SortDirection>('desc');
  const [inviteSortKey, setInviteSortKey] = useState<InviteSortKey>('expires');
  const [inviteSortDirection, setInviteSortDirection] = useState<SortDirection>('desc');

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getManagementToken]
  );

  const requestRows = useMemo<JoinRequestRow[]>(
    () =>
      joinRequests.map((request) => ({
        id: request.id,
        user: request.requestedByEmail || request.requestedByAuth0Sub,
        role: request.requestedRoleKey,
        status: request.status,
        created: request.createdAt,
        message: request.message || '-',
        request,
      })),
    [joinRequests]
  );

  const inviteRows = useMemo<InviteRow[]>(
    () =>
      invites.map((invite) => ({
        id: invite.id,
        email: invite.email,
        role: invite.roleKey,
        status: invite.status,
        expires: invite.expiresAt,
        invite,
      })),
    [invites]
  );

  const filteredRequestRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return requestRows.filter((row) => {
      if (rowTypeFilter === 'invites') {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [row.user, row.role, row.status, row.message, row.created].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [requestRows, rowTypeFilter, searchValue]);

  const filteredInviteRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return inviteRows.filter((row) => {
      if (rowTypeFilter === 'requests') {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [row.email, row.role, row.status, row.expires].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [inviteRows, rowTypeFilter, searchValue]);

  const sortedRequestRows = useMemo(() => {
    const direction = requestSortDirection === 'asc' ? 1 : -1;
    return [...filteredRequestRows].sort((left, right) => {
      const leftValue = String(left[requestSortKey]).toLowerCase();
      const rightValue = String(right[requestSortKey]).toLowerCase();
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });
  }, [filteredRequestRows, requestSortDirection, requestSortKey]);

  const sortedInviteRows = useMemo(() => {
    const direction = inviteSortDirection === 'asc' ? 1 : -1;
    return [...filteredInviteRows].sort((left, right) => {
      const leftValue = String(left[inviteSortKey]).toLowerCase();
      const rightValue = String(right[inviteSortKey]).toLowerCase();
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });
  }, [filteredInviteRows, inviteSortDirection, inviteSortKey]);

  const requestPageSize = 8;
  const invitePageSize = 8;

  const requestTotalPages = Math.max(1, Math.ceil(sortedRequestRows.length / requestPageSize));
  const inviteTotalPages = Math.max(1, Math.ceil(sortedInviteRows.length / invitePageSize));

  useEffect(() => {
    if (requestPage > requestTotalPages) {
      setRequestPage(requestTotalPages);
    }
  }, [requestPage, requestTotalPages]);

  useEffect(() => {
    if (invitePage > inviteTotalPages) {
      setInvitePage(inviteTotalPages);
    }
  }, [invitePage, inviteTotalPages]);

  const pagedRequestRows = useMemo(() => {
    const start = (requestPage - 1) * requestPageSize;
    return sortedRequestRows.slice(start, start + requestPageSize);
  }, [requestPage, sortedRequestRows]);

  const pagedInviteRows = useMemo(() => {
    const start = (invitePage - 1) * invitePageSize;
    return sortedInviteRows.slice(start, start + invitePageSize);
  }, [invitePage, sortedInviteRows]);

  const selectedRequest = useMemo(
    () => joinRequests.find((request) => request.id === selectedRequestId) ?? null,
    [joinRequests, selectedRequestId]
  );

  const selectedInvite = useMemo(
    () => invites.find((invite) => invite.id === selectedInviteId) ?? null,
    [invites, selectedInviteId]
  );

  useEffect(() => {
    if (selectedKind === 'request' && selectedRequest) {
      return;
    }

    if (selectedKind === 'invite' && selectedInvite) {
      return;
    }

    if (joinRequests.length > 0) {
      setSelectedKind('request');
      setSelectedRequestId(joinRequests[0].id);
      setSelectedInviteId(null);
      return;
    }

    if (invites.length > 0) {
      setSelectedKind('invite');
      setSelectedInviteId(invites[0].id);
      setSelectedRequestId(null);
      return;
    }

    setSelectedKind(null);
    setSelectedRequestId(null);
    setSelectedInviteId(null);
  }, [invites, joinRequests, selectedInvite, selectedKind, selectedRequest]);

  const setSelectedRequest = (request: JoinRequestItem) => {
    setSelectedKind('request');
    setSelectedRequestId(request.id);
    setSelectedInviteId(null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const setSelectedInvite = (invite: InviteItem) => {
    setSelectedKind('invite');
    setSelectedInviteId(invite.id);
    setSelectedRequestId(null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const [myResponse, allResponse] = await Promise.all([
        withAuth({ method: 'get', url: `${apiBaseUrl}/my` }),
        withAuth({ method: 'get', url: `${apiBaseUrl}/all` }).catch(() => null)
      ]);

      const myOrgs = (myResponse.data ?? []) as MyOrganization[];
      const allOrgs = (allResponse?.data ?? []) as OrganizationSummary[];

      setMyOrganizations(myOrgs);
      setAllOrganizations(allOrgs);

      const allowedOrganizations = myOrgs.filter((organization) => hasRequestAccessRole(organization.roles));
      const defaultAllowedOrg =
        allowedOrganizations.find((organization) => organization.isDefault)?.organizationId ??
        allowedOrganizations[0]?.organizationId ??
        null;
      const resolved = defaultAllowedOrg;

      setSelectedOrganizationId(prev => prev ?? resolved);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  const loadJoinRequestsAndInvites = useCallback(async (organizationId: number) => {
    try {
      setLoading(true);
      clearMessages();

      const [requestsResponse, invitesResponse] = await Promise.all([
        withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/join-requests/pending` }),
        withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/invites` })
      ]);

      setJoinRequests((requestsResponse.data ?? []) as JoinRequestItem[]);
      setInvites((invitesResponse.data ?? []) as InviteItem[]);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load requests/invites.';
      setError(message);
      setJoinRequests([]);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadOrganizations();
  }, [isAuthenticated, loadOrganizations]);

  useEffect(() => {
    if (!isAuthenticated || !selectedOrganizationId) {
      setJoinRequests([]);
      setInvites([]);
      return;
    }

    loadJoinRequestsAndInvites(selectedOrganizationId);
  }, [isAuthenticated, selectedOrganizationId, loadJoinRequestsAndInvites]);

  const handleApproveRequest = async (requestId: number, roleKey: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/join-requests/${requestId}/approve`,
        data: { approvedRoleKey: roleKey }
      });

      setNotice('Join request approved.');
      await loadJoinRequestsAndInvites(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to approve request.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/join-requests/${requestId}/reject`,
        data: {}
      });

      setNotice('Join request rejected.');
      await loadJoinRequestsAndInvites(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to reject request.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    if (!inviteEmail.trim()) {
      setError('Invite email is required.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/invites`,
        data: {
          email: inviteEmail.trim(),
          roleKey: inviteRoleKey
        }
      });

      setInviteEmail('');
      setInviteRoleKey('Student');
      setNotice('Invite created and email queued.');
      await loadJoinRequestsAndInvites(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to create invite.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (invite: InviteItem) => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/invites`,
        data: {
          email: invite.email,
          roleKey: invite.roleKey,
        },
      });

      setNotice(`Invite resent to ${invite.email}.`);
      await loadJoinRequestsAndInvites(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to resend invite.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRequestSort = (columnKey: string) => {
    if (!['user', 'role', 'status', 'created'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as RequestSortKey;
    if (requestSortKey === nextKey) {
      setRequestSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setRequestSortKey(nextKey);
    setRequestSortDirection('asc');
  };

  const handleInviteSort = (columnKey: string) => {
    if (!['email', 'role', 'status', 'expires'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as InviteSortKey;
    if (inviteSortKey === nextKey) {
      setInviteSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setInviteSortKey(nextKey);
    setInviteSortDirection('asc');
  };

  const requestColumns = useMemo<DataGridColumn<JoinRequestRow>[]>(() => {
    const columns: DataGridColumn<JoinRequestRow>[] = [
      {
        key: 'user',
        label: 'User',
        sortable: true,
        width: isSmallScreen ? '52%' : '32%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.user}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>{row.message}</div>
          </div>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        sortable: true,
        width: isSmallScreen ? '18%' : '14%',
        render: (row) => row.role,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '18%' : '12%',
        render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: '#f4f8ff', border: '1px solid #d1ddf3', color: '#35568a', padding: '2px 8px', fontSize: '0.73rem', fontWeight: 700 }}>
            {row.status}
          </span>
        ),
      },
    ];

    if (!isSmallScreen) {
      columns.push({
        key: 'created',
        label: 'Created',
        sortable: true,
        width: '18%',
        render: (row) => new Date(row.created).toLocaleString(),
      });
    }

    return columns;
  }, [isSmallScreen]);

  const inviteColumns = useMemo<DataGridColumn<InviteRow>[]>(() => {
    const columns: DataGridColumn<InviteRow>[] = [
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        width: isSmallScreen ? '52%' : '35%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.email}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>Invite sent</div>
          </div>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        sortable: true,
        width: isSmallScreen ? '18%' : '15%',
        render: (row) => row.role,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '18%' : '12%',
        render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: '#f4f8ff', border: '1px solid #d1ddf3', color: '#35568a', padding: '2px 8px', fontSize: '0.73rem', fontWeight: 700 }}>
            {row.status}
          </span>
        ),
      },
    ];

    if (!isSmallScreen) {
      columns.push({
        key: 'expires',
        label: 'Expires',
        sortable: true,
        width: '18%',
        render: (row) => new Date(row.expires).toLocaleString(),
      });
    }

    return columns;
  }, [isSmallScreen]);

  const requestRowActions: DataGridRowAction<JoinRequestRow>[] = [
    {
      key: 'approve',
      label: 'Approve',
      icon: <IconCheckCircle size={14} />,
      onClick: (row) => {
        void handleApproveRequest(row.request.id, row.request.requestedRoleKey);
      },
      isDisabled: () => saving,
    },
    {
      key: 'reject',
      label: 'Reject',
      icon: <IconDelete size={14} />,
      tone: 'destructive',
      onClick: (row) => {
        setPendingRejectRequestId(row.request.id);
      },
      isDisabled: () => saving,
    },
  ];

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'type',
        label: 'View',
        value: rowTypeFilter,
        onChange: (value) => setRowTypeFilter(value as RowTypeFilter),
        options: [
          { value: 'all', label: 'All items' },
          { value: 'requests', label: 'Join requests' },
          { value: 'invites', label: 'Invites' },
        ],
      },
    ],
    [rowTypeFilter]
  );

  const actions: HeaderAction[] = [
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadOrganizations();
        if (selectedOrganizationId) {
          void loadJoinRequestsAndInvites(selectedOrganizationId);
        }
      },
      disabled: saving,
    },
  ];

  const totalRequests = joinRequests.length;
  const totalInvites = invites.length;
  const pendingRequests = joinRequests.filter((request) => request.status.toLowerCase() === 'pending').length;
  const acceptedInvites = invites.filter((invite) => invite.acceptedAt != null).length;

  const selectedRequestRow = selectedRequest ? requestRows.find((row) => row.id === selectedRequest.id) ?? null : null;
  const selectedInviteRow = selectedInvite ? inviteRows.find((row) => row.id === selectedInvite.id) ?? null : null;

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Organization Requests & Invites</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage requests and invites.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Organization Memberships', to: '/manage-organization-memberships' },
          { label: 'Requests & Invites', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title="Organization Requests & Invites"
        subtitle={`${pendingRequests} pending requests • ${totalInvites} invites • ${acceptedInvites} accepted`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Requests" value={totalRequests} icon={<IconBook size={15} />} />
        <AdminStatCard label="Pending" value={pendingRequests} icon={<IconCheckCircle size={15} />} accent="active" />
        <AdminStatCard label="Invites" value={totalInvites} icon={<IconSpark size={15} />} accent="draft" />
        <AdminStatCard label="Accepted" value={acceptedInvites} icon={<IconArchive size={15} />} accent="archived" />
      </div>

      <section style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)', padding: 14 }}>
        <div style={{ display: 'grid', gap: 10 }}>
          <AdminSectionHeading icon={<IconMetadata size={14} />} title="Send Invite" />
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr) auto' }}>
            <input
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="user@example.com"
            />
            <select value={inviteRoleKey} onChange={(event) => setInviteRoleKey(event.target.value)}>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <button
              type="button"
              className="amd-btn amd-btn-primary"
              onClick={() => {
                void handleCreateInvite();
              }}
              disabled={saving || !selectedOrganizationId}
            >
              Send Invite
            </button>
          </div>
        </div>
      </section>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => setSearchValue(value)}
        searchPlaceholder="Search requests or invites..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && (
          <div style={{ display: 'grid', gap: 12, minHeight: 0 }}>
            <section style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)' }}>
              <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
                <AdminSectionHeading icon={<IconRelationships size={14} />} title="Pending Join Requests" />
              </div>
              <div style={{ padding: 14 }}>
                {loading ? (
                  <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>Loading requests...</div>
                ) : (
                  <AdminDataGrid<JoinRequestRow>
                    rows={pagedRequestRows}
                    columns={requestColumns}
                    selectedRowId={selectedRequestId}
                    onRowClick={(row) => setSelectedRequest(row.request)}
                    selectedIds={requestSelectedIds}
                    onSelectedIdsChange={setRequestSelectedIds}
                    showSelection={!isSmallScreen}
                    sortKey={requestSortKey}
                    sortDirection={requestSortDirection}
                    onSort={handleRequestSort}
                    page={requestPage}
                    pageSize={requestPageSize}
                    totalCount={sortedRequestRows.length}
                    onPageChange={setRequestPage}
                    emptyMessage="No pending requests."
                    rowActions={requestRowActions}
                  />
                )}
              </div>
            </section>

            <section style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)' }}>
              <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
                <AdminSectionHeading icon={<IconSpark size={14} />} title="Recent Invites" />
              </div>
              <div style={{ padding: 14 }}>
                {loading ? (
                  <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>Loading invites...</div>
                ) : (
                  <AdminDataGrid<InviteRow>
                    rows={pagedInviteRows}
                    columns={inviteColumns}
                    selectedRowId={selectedInviteId}
                    onRowClick={(row) => setSelectedInvite(row.invite)}
                    selectedIds={inviteSelectedIds}
                    onSelectedIdsChange={setInviteSelectedIds}
                    showSelection={!isSmallScreen}
                    sortKey={inviteSortKey}
                    sortDirection={inviteSortDirection}
                    onSort={handleInviteSort}
                    page={invitePage}
                    pageSize={invitePageSize}
                    totalCount={sortedInviteRows.length}
                    onPageChange={setInvitePage}
                    emptyMessage="No invites yet."
                  />
                )}
              </div>
            </section>
          </div>
        )}

        {showDetailsPanel && (
          <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button type="button" className="amd-btn amd-btn-ghost" style={{ marginBottom: 8 }} onClick={() => setMobilePane('list')}>
                  Back to List
                </button>
              )}
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '1.02rem',
                  fontWeight: 800,
                  color: '#173159',
                  background: selectedKind === 'request' || selectedKind === 'invite' ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: selectedKind ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                  borderRadius: 10,
                  padding: '9px 11px',
                }}
              >
                {selectedKind === 'request'
                  ? 'Join Request Details'
                  : selectedKind === 'invite'
                  ? 'Invite Details'
                  : selectedOrganizationId
                  ? 'Organization Actions'
                  : 'Select an organization'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ display: 'grid', gap: 18 }}>
                {selectedRequestRow && (
                  <section style={{ display: 'grid', gap: 10, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                    <AdminSectionHeading icon={<IconCheckCircle size={14} />} title="Selected Request" />
                    <div style={{ display: 'grid', gap: 6, fontSize: '0.82rem', color: '#586f96' }}>
                      <div>User: <strong>{selectedRequestRow.user}</strong></div>
                      <div>Requested Role: <strong>{selectedRequestRow.role}</strong></div>
                      <div>Status: <strong>{selectedRequestRow.status}</strong></div>
                      <div>Message: <strong>{selectedRequestRow.message || '-'}</strong></div>
                      <div>Created: <strong>{new Date(selectedRequestRow.created).toLocaleString()}</strong></div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="amd-btn amd-btn-secondary"
                        onClick={() => {
                          void handleApproveRequest(selectedRequestRow.request.id, selectedRequestRow.request.requestedRoleKey);
                        }}
                        disabled={saving}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="amd-btn amd-btn-ghost"
                        onClick={() => setPendingRejectRequestId(selectedRequestRow.request.id)}
                        disabled={saving}
                      >
                        Reject
                      </button>
                    </div>
                  </section>
                )}

                {selectedInviteRow && (
                  <section style={{ display: 'grid', gap: 10 }}>
                    <AdminSectionHeading icon={<IconArchive size={14} />} title="Selected Invite" />
                    <div style={{ display: 'grid', gap: 6, fontSize: '0.82rem', color: '#586f96' }}>
                      <div>Email: <strong>{selectedInviteRow.email}</strong></div>
                      <div>Role: <strong>{selectedInviteRow.role}</strong></div>
                      <div>Status: <strong>{selectedInviteRow.status}</strong></div>
                      <div>Expires: <strong>{new Date(selectedInviteRow.expires).toLocaleString()}</strong></div>
                      <div>Accepted: <strong>{selectedInviteRow.invite.acceptedAt ? new Date(selectedInviteRow.invite.acceptedAt).toLocaleString() : '-'}</strong></div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="amd-btn amd-btn-secondary"
                        onClick={() => {
                          void handleResendInvite(selectedInviteRow.invite);
                        }}
                        disabled={saving || selectedInviteRow.status.toLowerCase() === 'accepted'}
                        title={selectedInviteRow.status.toLowerCase() === 'accepted' ? 'Accepted invites do not need resend.' : undefined}
                      >
                        Resend Invite
                      </button>
                    </div>
                  </section>
                )}

                {!selectedRequestRow && !selectedInviteRow && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    <AdminSectionHeading icon={<IconBook size={14} />} title="Getting Started" />
                    <div
                      style={{
                        minHeight: 140,
                        border: '1px dashed #c9d6ef',
                        borderRadius: 12,
                        background: '#f8fbff',
                        display: 'grid',
                        placeContent: 'center',
                        textAlign: 'center',
                        color: '#4f658f',
                        gap: 8,
                        padding: 16,
                      }}
                    >
                      <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconSpark size={24} /></div>
                      <div style={{ fontWeight: 700 }}>Select a request or invite from the list</div>
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingRejectRequestId != null}
        title="Reject join request"
        message="Reject this join request? This action cannot be undone."
        onClose={() => setPendingRejectRequestId(null)}
        actions={[
          {
            label: 'Cancel',
            variant: 'default',
            onClick: () => setPendingRejectRequestId(null),
            disabled: saving,
          },
          {
            label: 'Reject',
            variant: 'destructive',
            disabled: saving || pendingRejectRequestId == null,
            onClick: () => {
              if (pendingRejectRequestId == null) {
                return;
              }

              void handleRejectRequest(pendingRejectRequestId);
              setPendingRejectRequestId(null);
            },
          },
        ]}
      />
    </div>
  );
};

export default ManageOrganizationRequests;
